import { Alert, List, Typography } from "antd"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { PendingSignaturesAlert } from "../../Esign/components/PendingSignaturesAlert.tsx"
import type {
  PendingCommitment,
  PendingSignature
} from "../api/services.types.ts"

const { Text } = Typography

interface PendingCommitmentsAlertProps {
  commitments: PendingCommitment[]
  signatures?: PendingSignature[]
  // The Archive dialog asks about the meetings calmly; the status picker
  // keeps its error tone.
  type?: "error" | "warning"
}

/** Shared by the Archive modal and the status picker, so the two cannot drift. */
export function PendingCommitmentsAlert({
  commitments,
  signatures = [],
  type = "error"
}: PendingCommitmentsAlertProps) {
  const { t } = useTranslation()
  const { formatDate } = useDateFormatter()

  return (
    <>
      {commitments.length > 0 && (
        <Alert
          type={type}
          showIcon
          className="mb-4"
          data-testid="archive-pending-commitments"
          message={t("projectArchive.commitments.title")}
          description={
            <>
              <div className="mb-2">
                {t("projectArchive.commitments.body", {
                  count: commitments.length
                })}
              </div>
              <List
                size="small"
                dataSource={commitments}
                renderItem={(commitment) => (
                  <List.Item className="!px-0">
                    <div className="flex flex-col">
                      <Text strong>{commitment.title}</Text>
                      <Text type="secondary" className="text-xs">
                        {formatDate(commitment.start_at)}
                        {commitment.has_external_invitees
                          ? ` · ${t("projectArchive.commitments.externalInvitee")}`
                          : ""}
                      </Text>
                    </div>
                  </List.Item>
                )}
              />
            </>
          }
        />
      )}
      {signatures.length > 0 && (
        <PendingSignaturesAlert signatures={signatures} />
      )}
    </>
  )
}
